import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CButton, CCard, CCardBody, CCol, CFormInput, CFormSelect, CRow, CSpinner } from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilReload } from '@coreui/icons';
import { useNavigate } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { useFleet } from '../../contexts/FleetContext';
import { CourierState, FleetLive } from '../../types/fleet';
import { FleetCourierRoute } from '../../types/deliveryRoute';
import { formatCurrency } from '../orders/orderUtils';
import { FleetGuard, FleetHeader } from './FleetHeader';
import FleetMap from './FleetMap';
import { RouteLegend, plannedSummary } from './CourierRouteLines';
import { COURIER_STATES, CourierStateBadge, DelayIndicator, formatMinutes } from './fleetUtils';

const REFRESH_MS = 15000;

const KPI_ORDER: { key: keyof FleetLive['summary']; label: string; color: string }[] = [
    { key: 'connected', label: 'Conectados', color: '#18181B' },
    { key: 'busy', label: 'En pedido', color: COURIER_STATES.busy.color },
    { key: 'available', label: 'Libres', color: COURIER_STATES.available.color },
    { key: 'no_balance', label: 'Sin saldo', color: COURIER_STATES.no_balance.color },
    { key: 'offline', label: 'Desconectados', color: COURIER_STATES.offline.color },
    { key: 'unassigned_alerts', label: 'Sin domiciliario', color: '#EF4444' },
];

const FleetLiveContent: React.FC = () => {
    const navigate = useNavigate();
    const { company } = useFleet();
    const [data, setData] = useState<FleetLive | null>(null);
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [search, setSearch] = useState('');
    const [stateFilter, setStateFilter] = useState<'' | CourierState>('');

    const load = useCallback(
        (silent = false) => {
            if (!company) return;
            if (!silent) setLoading(true);
            DomicilappService.getFleetLive(company.id)
                .then((response) => {
                    if (response?.status === 200) {
                        setData(response.data);
                        setLastUpdate(new Date());
                    }
                })
                .catch(() => undefined)
                .finally(() => !silent && setLoading(false));
        },
        [company],
    );

    useEffect(() => {
        setData(null);
        setSelectedId(null);
        load();
        const interval = setInterval(() => load(true), REFRESH_MS);
        return () => clearInterval(interval);
    }, [load]);

    const couriers = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (data?.couriers ?? []).filter(
            (c) => (!stateFilter || c.state === stateFilter) && (!term || c.name.toLowerCase().includes(term) || (c.phone ?? '').includes(term)),
        );
    }, [data, search, stateFilter]);

    const selected = data?.couriers.find((c) => c.id === selectedId) ?? null;

    // Ruta del domiciliario elegido: se pide al elegirlo y cuando llega una ubicación nueva en el refresco de la flota
    const [route, setRoute] = useState<FleetCourierRoute | null>(null);
    const selectedDeliveryId = selected?.current_delivery?.id;
    const selectedLocationAt = selected?.location_updated_at;
    useEffect(() => {
        if (!company || !selectedId || !selectedDeliveryId) {
            setRoute(null);
            return;
        }
        let cancelled = false;
        DomicilappService.getFleetCourierRoute(company.id, selectedId)
            .then((response) => !cancelled && setRoute(response?.status === 200 ? response.data : null))
            .catch(() => !cancelled && setRoute(null));
        return () => {
            cancelled = true;
        };
    }, [company, selectedId, selectedDeliveryId, selectedLocationAt]);

    return (
        <>
            <FleetHeader
                title="Flota en vivo"
                description="Dónde está cada domiciliario y qué está haciendo"
                alerts={data?.alerts}
                actions={
                    <>
                        {lastUpdate && <small className="text-body-secondary text-nowrap">Actualizado {lastUpdate.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</small>}
                        <CButton size="sm" color="light" onClick={() => load()} disabled={loading}>
                            <CIcon icon={cilReload} size="sm" className="me-1" /> Actualizar
                        </CButton>
                    </>
                }
            />

            {/* KPIs */}
            <CRow className="g-2 mb-3">
                {KPI_ORDER.map((kpi) => (
                    <CCol key={kpi.key} xs={6} md={4} xl={2}>
                        <CCard className="h-100">
                            <CCardBody className="py-2 px-3">
                                <small className="text-body-secondary d-block">{kpi.label}</small>
                                <span className="fs-4 fw-semibold" style={{ color: kpi.color }}>{data ? data.summary[kpi.key] : '—'}</span>
                            </CCardBody>
                        </CCard>
                    </CCol>
                ))}
            </CRow>

            <CRow className="g-3">
                <CCol lg={8}>
                    <CCard>
                        <CCardBody className="p-2">
                            {loading && !data ? (
                                <div className="text-center py-5"><CSpinner size="sm" /></div>
                            ) : (
                                <>
                                    <FleetMap couriers={data?.couriers ?? []} selectedCourierId={selectedId} onSelectCourier={setSelectedId} route={route} />
                                    {route && <RouteLegend destinationHidden={route.planned?.destination_hidden} />}
                                </>
                            )}
                        </CCardBody>
                    </CCard>
                </CCol>

                <CCol lg={4}>
                    <CCard className="h-100">
                        <CCardBody className="p-3 d-flex flex-column" style={{ maxHeight: 560 }}>
                            <div className="d-flex gap-2 mb-2">
                                <CFormInput size="sm" placeholder="Buscar domiciliario..." value={search} onChange={(e) => setSearch(e.target.value)} />
                                <CFormSelect size="sm" value={stateFilter} onChange={(e) => setStateFilter(e.target.value as CourierState | '')} style={{ maxWidth: 150 }}>
                                    <option value="">Todos</option>
                                    {(Object.keys(COURIER_STATES) as CourierState[]).map((s) => (
                                        <option key={s} value={s}>{COURIER_STATES[s].label}</option>
                                    ))}
                                </CFormSelect>
                            </div>

                            <div className="flex-grow-1 overflow-auto">
                                {couriers.length === 0 ? (
                                    <p className="text-body-secondary small text-center py-4 mb-0">
                                        {data ? 'No hay domiciliarios con ese filtro.' : 'Cargando...'}
                                    </p>
                                ) : (
                                    couriers.map((courier) => (
                                        <div
                                            key={courier.id}
                                            role="button"
                                            onClick={() => setSelectedId(courier.id)}
                                            className={`rounded-2 px-2 py-2 mb-1 ${courier.id === selectedId ? 'bg-body-secondary' : ''}`}
                                        >
                                            <div className="d-flex justify-content-between align-items-center gap-2">
                                                <span className="fw-semibold text-body text-truncate">{courier.name}</span>
                                                <CourierStateBadge state={courier.state} />
                                            </div>
                                            <small className="text-body-secondary d-block text-truncate">
                                                {courier.current_delivery
                                                    ? `Entrega #${courier.current_delivery.id} · ${courier.current_delivery.status_name} · ${courier.current_delivery.store.name}`
                                                    : courier.location_age_minutes !== null
                                                        ? `Ubicación hace ${formatMinutes(courier.location_age_minutes)}`
                                                        : 'Sin ubicación reportada'}
                                            </small>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Detalle del domiciliario elegido */}
                            {selected && (
                                <div className="border-top pt-3 mt-2 small">
                                    <div className="d-flex justify-content-between mb-1">
                                        <span className="fw-semibold text-body">{selected.name}</span>
                                        <a href={`tel:${selected.phone}`} className="text-body-secondary">{selected.phone}</a>
                                    </div>
                                    <div className="text-body-secondary">Saldo: <span className="text-body fw-semibold">{formatCurrency(selected.balance)}</span></div>
                                    <div className="text-body-secondary">
                                        App: {selected.on_turn ? `en turno${selected.turn_device_model ? ` (${selected.turn_device_model})` : ''}` : 'sin turno activo'}
                                        {selected.location_age_minutes !== null ? ` · ubicación hace ${formatMinutes(selected.location_age_minutes)}` : ''}
                                    </div>
                                    {selected.blocked_at && <div className="text-danger">Bloqueado: {selected.blocked_reason || 'sin motivo'}</div>}
                                    {selected.current_delivery && (
                                        <div className="mt-2 p-2 rounded-2 bg-body-secondary">
                                            <div className="d-flex justify-content-between">
                                                <span className="fw-semibold text-body">Entrega #{selected.current_delivery.id}</span>
                                                <DelayIndicator delivery={selected.current_delivery} />
                                            </div>
                                            <div className="text-body-secondary">{selected.current_delivery.status_name} · {selected.current_delivery.store.name}</div>
                                            <div className="text-body-secondary">
                                                Destino: {selected.current_delivery.destination.municipality ?? '—'}
                                                {selected.current_delivery.destination.distance_km !== null ? ` · ${selected.current_delivery.destination.distance_km} km` : ''}
                                            </div>
                                            {route?.planned && (
                                                <div className="text-body-secondary">
                                                    Ruta recomendada: <span className="text-body fw-semibold">{plannedSummary(route.planned).estimate || '—'}</span>
                                                    <span className="d-block">{plannedSummary(route.planned).computedAgo}</span>
                                                </div>
                                            )}
                                            <CButton size="sm" color="light" className="mt-2" onClick={() => navigate('/fleet/deliveries')}>
                                                Ver pedidos
                                            </CButton>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>
        </>
    );
};

const FleetLivePage: React.FC = () => (
    <FleetGuard>
        <FleetLiveContent />
    </FleetGuard>
);

export default FleetLivePage;
